import React, { Fragment, useEffect, useRef, useState } from 'react';

import { XMarkIcon } from '@heroicons/react/20/solid';
import { showToast } from '../../shared/Toast';
import { CurrentUser } from '../../calendar/Calendar';
import { useForm } from 'react-hook-form';
import { InvitePerformerModel, ProjectModel, SbCurrentUser, SubmissionBoardModel } from '../component/SbModel';
import { Dialog, Transition } from '@headlessui/react';
import submissionBoardService from '../../services/SubmissionBoardService';
import Button from '../component/Button';

export interface InvitePerformerProps {
  currentUser: SbCurrentUser;
  project: ProjectModel;
  submissionBoardModel: SubmissionBoardModel;
  dialogCloseHandler?: (open: boolean, postData: any) => void;
}

export default function InvitePerformer({
  currentUser,
  project,
  submissionBoardModel,
  dialogCloseHandler,
}: InvitePerformerProps) {
  const [open, setOpen] = useState(true);
  const emailInputRef = useRef(null);

  const onCloseDialog = (value: boolean) => {
    dialogCloseHandler(value, null);
  };

  useEffect(() => {
    // Access the input element directly using its stable ID
    const emailElement = document.getElementById('email');
    if (emailElement) {
      // Assert that the element is an HTMLInputElement before calling focus
      (emailElement as HTMLInputElement).focus();
    }
  }, []);
  const handleFormSubmit = async (data) => {
    try {
      console.log('data', data);
      if (data.first_name == '') {
        showToast('Invalid Data: First name is required', 'danger');
        return;
      }
      if (data.last_name == '') {
        showToast('Invalid Data: Last name is required', 'danger');
        return;
      }
      let postData = {
        ...data,
      };
      const response = await submissionBoardService.invitePerformer(
        project.uuid,
        submissionBoardModel.id,
        postData
      );
      if (response.data && 'error' in response.data) {
        showToast(response.data.error, 'danger');
        return;
      }
      // const url = url_api_v1_sb_profile_invite
      //   .replace(':sb_id', submissionBoardModel.id.toString())
      //   .replace(':project_uuid', project.uuid);
      // const response = await api.post(url, postData);
      // console.log(response);
      // if (response.status !== 200 && response.status !== 201) {
      //   if (response.data && 'error' in response.data) {
      //     showToast(response.data.error, 'danger');
      //     return;
      //   }
      //   throw new Error('Error updating job.');
      // }
      showToast('Invited ' + data.email, 'success');
      dialogCloseHandler(false, postData);
    } catch (error) {
      if (error?.response?.data && 'error' in error.response?.data) {
        showToast(error.response.data.error, 'danger');
        return;
      }

      showToast('Error in inviting user to submission board', 'danger');
    }
  };

  const {
    control,
    watch,
    getValues,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<InvitePerformerModel>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
  });

  useEffect(() => { }, [project]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onCloseDialog(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* The backdrop */}
          <div className="fixed inset-0 bg-customGray" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center px-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="absolute flex flex-col bg-white w-full rounded-md max-w-sm h-auto max-h-screen p-4">
                <div className="flex items-center justify-between px-0 py-2 ">
                  <h2 className="text-lg font-medium text-gray-900">Invite New Talent</h2>
                  <button
                    id={'inviteprofiles_close_model_id'}
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md"
                    onClick={() => onCloseDialog(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                  </button>
                </div>

                <div
                  className="mt-4 text-left"
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div className="pb-10 px-10">
                    <form onSubmit={handleSubmit(handleFormSubmit)} className='gap-5 flex flex-col'>
                      <div className=" gap-1 flex flex-col">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Performer's Email*
                        </label>
                        <input
                          type="text"
                          name="email"
                          id="email"
                          placeholder="Enter Performer's email address"
                          className="block w-72 h-10 bg-white shadow-sm border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-10 focus:border-blue-500 placeholder-gray-400"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                              message: 'Please enter a valid email address',
                            },
                          })}
                        />
                        {errors.email && (
                          <p className="text-red-900 text-xs mt-1">{errors?.email?.message}</p>
                        )}
                      </div>

                      <div className=" gap-1 flex flex-col">
                        <label
                          htmlFor="first_name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name*
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          id="first_name"
                          placeholder="First name"
                          className="block w-72 h-10 bg-white shadow-sm border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-10 focus:border-blue-500 placeholder-gray-400"
                          {...register('first_name', { required: 'First name is required' })}
                        />
                        {errors.first_name && (
                          <p className="text-red-900 text-xs">{errors?.first_name?.message}</p>
                        )}
                      </div>

                      <div className=" gap-1 flex flex-col">
                        <label
                          htmlFor="last_name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name*
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          id="last_name"
                          placeholder="Last name"
                          className="block w-72 h-10 bg-white shadow-sm border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-10 focus:border-blue-500 placeholder-gray-400"
                          {...register('last_name', { required: 'Last name is required' })}
                        />
                        {errors.last_name && (
                          <p className="text-red-900 text-xs">{errors?.last_name?.message}</p>
                        )}
                      </div>

                      <div className="flex flex-row justify-center items-center gap-2 text-center">
                        <Button
                          id="cancel_inviteperf_btn_id"
                          variant="secondary"
                          btn_size="small"
                          icon_position="hor"
                          type="button"
                          onClick={() => {
                            onCloseDialog(false);
                          }}
                        >
                          <span className="truncate" style={{ maxWidth: '10rem' }}>
                            Cancel
                          </span>
                        </Button>
                        <Button
                          id="submit_inviteperf_btn_id"
                          variant="primary"
                          btn_size="small"
                          icon_position="hor"
                          type="submit"
                        >
                          <span className="truncate" style={{ maxWidth: '10rem' }}>
                            Invite
                          </span>
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
